import React, { useState, useMemo } from "react";
import "banner-pattern/dist/main.css";
import { FieldConfig } from "../../../types/formTypes";
import StepIndicator, {
  StepStatus,
  StepIndicatorItem,
} from "../../StepIndicator/StepIndicator";
import { applySteps } from "../../../config/steps/citizenshipApplyStepsConfig";
import { tuitionSteps } from "../../../config/steps/tuitionStepsConfig";
import BreadCrumbs from "../BreadCrumbs/BreadCrumbs";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { clearForm } from "../../../store/slices/Citizenship/citizenshipFormSlice";
import { EligibilityBannerContext } from "./EligibilityBannerContext";
interface StepFields {
  step1: FieldConfig[];
  step2: FieldConfig[];
  step3: FieldConfig[];
  step4?: FieldConfig[];
}

interface StepTitles {
  step1: string;
  step2: string;
  step3: string;
  step4?: string;
}

interface FormStepperProps {
  stepFields: StepFields;
  stepTitles: StepTitles;
  totalSteps: number;
  renderForm: (
    stepFields: FieldConfig[],
    stepTitle: string,
    buttons: string[],
    onNext?: () => void,
    onBack?: () => void,
    onClear?: () => void,
    currentStep?: number,
  ) => React.ReactNode;
  showSuccess?: boolean;
  successContent?: React.ReactNode;
  stepLabels?: string[]; // Custom step labels for different flows
  patchErrorBanner?: React.ReactNode;
}

const FormStepper: React.FC<FormStepperProps> = ({
  stepFields,
  stepTitles,
  totalSteps,
  renderForm,
  showSuccess = false,
  successContent,
  stepLabels,
  patchErrorBanner,
}) => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const navigate = useNavigate();

  // Use provided stepLabels or fallback based on URL
  const steps =
    stepLabels ||
    (globalThis.location.pathname.includes("tuition")
      ? tuitionSteps
      : applySteps);

  const dispatch = useDispatch();

  const [resetKeys, setResetKeys] = useState<Record<number, number>>({});
  const [bannerVisible, setBannerVisible] = useState<boolean>(true);

  const eligibilityBannerContextValue = useMemo(() => ({
    visible: bannerVisible,
    dismissed: !bannerVisible,
    dismiss: () => setBannerVisible(false),   // one call, one place
  }), [bannerVisible]);

  const items: StepIndicatorItem[] = useMemo(() => {
    return steps.map((label, idx) => {
      let status: StepStatus = "inactive";
      if (idx + 1 < currentStep) status = "complete";
      else if (idx + 1 === currentStep) status = "active";
      return { label, status };
    });
  }, [currentStep, steps]);

  const getCurrentStepFields = (): FieldConfig[] => {
    switch (currentStep) {
      case 1:
        return stepFields.step1 ?? [];
      case 2:
        return stepFields.step2 ?? [];
      case 3:
        return stepFields.step3 ?? [];
      case 4:
        return stepFields.step4 ?? [];

      default:
        return [];
    }
  };

  // Get current step title
  const getCurrentStepTitle = (): string => {
    switch (currentStep) {
      case 1:
        return stepTitles.step1 ?? "";
      case 2:
        return stepTitles.step2 ?? "";
      case 3:
        return stepTitles.step3 ?? "";
      case 4:
        return stepTitles.step4 ?? "";
      default:
        return "";
    }
  };
  const getCurrentHeadTitle = (): string => {
    switch (currentStep) {
      case 1:
        return "Applicant Information";
      case 2:
        return "Employment Information";
      case 3:
        return "Fee Summary and Comments";
      case 4:
        return "Submit";
      default:
        return "";
    }
  };
  // Get buttons for current step
  const getStepButtons = (): string[] => {
    // Non-tuition flow (Citizenship)
    if (currentStep === 1) return ["continue"]; // Changed from ['save', 'next'] to single button
    if (currentStep === 2) return ["back", "next"];
    if (currentStep === 3) return ["clear", "back", "submit"];

    return [];
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClear = () => {
    // bannerVisible lives here, above the remount boundary.
    // Whether the Form remounts or not, bannerVisible is unchanged.
    // No guard needed — banner state is not affected by Clear.
    setResetKeys((prev) => ({
      ...prev,
      [currentStep]: (prev[currentStep] ?? 0) + 1,
    }));
  };

  const handleBreadcrumbPrevious = (): void => {
    navigate("/citizenship"); // Navigate back to the reimbursement overview page
    dispatch(clearForm());
  };

  return (
    <div className="grid-container max-w-7xl mx-auto px-4">
      <div className="form-stepper flex flex-col w-full">
        {/* Mobile step info */}
        <div className="flex justify-between items-center w-full min-[992px]:hidden mb-4">
          <p className="p3 -medium !text-base">{getCurrentHeadTitle()}</p>
          <div className="flex gap-2 text-sm">
            <span className="inline-link text-[#0074AD] underline  text-[13px]">
              Step {currentStep}
            </span>
            <span>|</span>
            <span className="inline-link text-[#0074AD] underline  text-[13px]">
              Step {totalSteps}
            </span>
          </div>
        </div>
        <BreadCrumbs
          previousPage="Back to reimbursement overview"
          currentPage="Apply"
          onPreviousClick={handleBreadcrumbPrevious}
        />
        <h1 className="ds-type__h2 mt-8 mb-0">Apply</h1>
        <div className="flex flex-col min-[992px]:flex-row gap-8 w-full">
          {/* Stepper (left) - hidden on small screens, visible on min-[992px] (1024px) and up */}
          <div className="hidden min-[992px]:block w-full min-[992px]:w-1/4 flex-shrink-0">
            <StepIndicator items={items} />
          </div>
          {/* Form or Success (right) - full width on tablet and below */}
          <div className="w-full min-[992px]:w-3/4 m-5">
            {showSuccess ? (
              successContent
            ) : (
              <>
                {patchErrorBanner && (
                  <div className="mb-6!">{patchErrorBanner}</div>
                )}

                <EligibilityBannerContext.Provider
                  value={eligibilityBannerContextValue}
                >
                  <div key={resetKeys[currentStep]}>
                    {renderForm(
                      getCurrentStepFields(),
                      getCurrentStepTitle(),
                      getStepButtons(),
                      handleNext,
                      handleBack,
                      handleClear,
                      currentStep,
                    )}
                  </div>
                </EligibilityBannerContext.Provider>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormStepper;
